import React, { useMemo, useState } from "react";
import { CUSTOMER_TYPES, CUSTOMER_STATUS } from "./orgConstants";
import { getOrgCustomers, normalizeCustomerRecord } from "./customerModel";

const PAGE_SIZE = 6;

function statusBadge(status) {
  const map = {
    Active: "success",
    Draft: "secondary",
    Inactive: "secondary",
    Blocked: "danger",
  };
  const v = map[status] || "secondary";
  return <span className={`badge rounded-pill text-bg-${v}`}>{status}</span>;
}

export default function CustomerListTab({
  org,
  onAddCustomer,
  onEditCustomer,
  onDeleteCustomer,
  onRefreshFromApi,
}) {
  /** Prefer `org.customers` so row indices match persistence; fallback to legacy embedded. */
  const rawList = useMemo(() => {
    if (!org) return [];
    if (org.customers?.length) return org.customers;
    return getOrgCustomers(org);
  }, [org]);

  const customers = useMemo(
    () => rawList.map((c) => normalizeCustomerRecord(c, org?.id)),
    [rawList, org?.id]
  );

  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterRegion, setFilterRegion] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [page, setPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    if (!onRefreshFromApi) return;
    setRefreshing(true);
    try {
      await onRefreshFromApi();
    } finally {
      setRefreshing(false);
    }
  };

  const indexedFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return customers
      .map((c, index) => ({ c, index }))
      .filter(({ c }) => {
        if (filterType && c.customerType !== filterType) return false;
        if (filterRegion && String(c.marketRegion || "") !== filterRegion) return false;
        if (filterStatus && c.customerStatus !== filterStatus) return false;
        if (!q) return true;
        return (
          String(c.customerCode || "").toLowerCase().includes(q) ||
          String(c.customerName || "").toLowerCase().includes(q) ||
          String(c.customerGroup || "").toLowerCase().includes(q)
        );
      });
  }, [customers, search, filterType, filterRegion, filterStatus]);

  const regions = useMemo(() => {
    const s = new Set(customers.map((c) => c.marketRegion).filter(Boolean));
    return [...s].sort();
  }, [customers]);

  const totalPages = Math.max(1, Math.ceil(indexedFiltered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const slice = indexedFiltered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  React.useEffect(() => {
    setPage(1);
  }, [search, filterType, filterRegion, filterStatus, org?.id]);

  React.useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  if (!org) {
    return (
      <div className="org-empty-state text-center py-5">
        <p className="text-muted small">Save the organization first to manage suppliers.</p>
      </div>
    );
  }

  return (
    <div className="customer-list-tab">
      <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 mb-3">
        <div>
          <h2 className="h6 fw-semibold mb-0">Suppliers</h2>
          <p className="text-muted small mb-0">Linked to {org.code} — {org.name}</p>
        </div>
        <div className="d-flex flex-wrap gap-2">
          {onRefreshFromApi ? (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <i className="bi bi-arrow-clockwise me-1" aria-hidden />
              {refreshing ? "Refreshing…" : "Refresh from server"}
            </button>
          ) : null}
          <button type="button" className="btn btn-primary btn-sm" onClick={onAddCustomer}>
            <i className="bi bi-plus-lg me-1" aria-hidden />
            Add supplier
          </button>
        </div>
      </div>

      <div className="row g-2 mb-3">
        <div className="col-md-4">
          <div className="input-group input-group-sm">
            <span className="input-group-text bg-white border-end-0">
              <i className="bi bi-search text-muted" />
            </span>
            <input type="search" className="form-control border-start-0" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
        <div className="col-auto">
          <select className="form-select form-select-sm" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="">All types</option>
            {CUSTOMER_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-auto">
          <select className="form-select form-select-sm" value={filterRegion} onChange={(e) => setFilterRegion(e.target.value)}>
            <option value="">All regions</option>
            {regions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
        <div className="col-auto">
          <select className="form-select form-select-sm" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">All statuses</option>
            {CUSTOMER_STATUS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="card border-0 shadow-sm rounded-3 overflow-hidden">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0 small">
            <thead className="table-light">
              <tr>
                <th>Supplier code</th>
                <th>Supplier name</th>
                <th>Type</th>
                <th>Group</th>
                <th>Sales channel</th>
                <th>Market / region</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {slice.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-5">
                    <div className="org-empty-state text-center">
                      <div className="org-empty-state__icon mb-2">
                        <i className="bi bi-people" aria-hidden />
                      </div>
                      <p className="fw-medium mb-1">No suppliers yet</p>
                      <p className="text-muted small mb-3">Add a supplier to start onboarding.</p>
                      <button type="button" className="btn btn-outline-primary btn-sm" onClick={onAddCustomer}>
                        + Add supplier
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                slice.map(({ c, index: globalIndex }) => (
                  <tr key={`${c.customerId || c.customerCode}-${globalIndex}`}>
                    <td>
                      <code>{c.customerCode}</code>
                    </td>
                    <td className="fw-medium">{c.customerName}</td>
                    <td>{c.customerType}</td>
                    <td>{c.customerGroup || "—"}</td>
                    <td>{c.salesChannel || "—"}</td>
                    <td>{c.marketRegion || "—"}</td>
                    <td>{statusBadge(c.customerStatus)}</td>
                    <td className="text-end text-nowrap">
                      <button type="button" className="btn btn-link btn-sm p-1" title="Edit" onClick={() => onEditCustomer(globalIndex)}>
                        <i className="bi bi-pencil-square text-primary" />
                      </button>
                      <button type="button" className="btn btn-link btn-sm p-1" title="Delete" onClick={() => onDeleteCustomer(globalIndex)}>
                        <i className="bi bi-trash text-danger" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {indexedFiltered.length > 0 && (
          <div className="card-footer bg-white border-top d-flex justify-content-between align-items-center py-2 small text-muted">
            <span>
              {(pageSafe - 1) * PAGE_SIZE + 1}–{Math.min(pageSafe * PAGE_SIZE, indexedFiltered.length)} of {indexedFiltered.length}
            </span>
            <div className="btn-group btn-group-sm">
              <button type="button" className="btn btn-sm btn-outline-secondary" disabled={pageSafe <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Prev
              </button>
              <button type="button" className="btn btn-sm btn-outline-secondary" disabled={pageSafe >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
